import React, { PropTypes } from "react";
import { FormattedMessage, intlShape } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link } from "react-router";

import { DataTable } from "../../../../common/DataTable";
import * as dataActions from "../../../../../utils/data/dataActions";
import { MEMBER_FILES, MEMBER_FILES_REGENERATION } from "../../../../../utils/data/models";
import { toast } from "react-toastify";
import simpleFetch from "../../../../../../utils/simpleFetch";
import { toDateOnly } from "../../../../common/dateHelper";
import { ACTIVE } from "../../enums/clientStatusTypes";

class MemberFilesDataTable extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            selectedMembers: new Set(),
        };

        this.getData = this.getData.bind(this);
        this.getCols = this.getCols.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handleRegenerateSelected = this.handleRegenerateSelected.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.memberFiles !== this.props.memberFiles) {
            this.setState({ selectedMembers: new Set() });
        }
    }

    componentWillUnmount() {
        dataActions.clearDataList(MEMBER_FILES);
    }

    /**
     * Renders user-friendly error messages based on exception type.
     * Maps specific backend exceptions to translated messages.
     */
    renderErrorMessage(member) {
        if (!member.error) {
            return <span>N/A</span>;
        }

        const error = member.error;

        // Handle specific exception types with user-friendly messages
        if (error.includes("NoActiveMemberCoverageException")) {
            return (
                <span title={error}>
                    <FormattedMessage id="memberFiles.dataTable.errors.noCoverageFound" />
                </span>
            );
        }

        if (error.includes("NoHealthOrTravelGuaranteeException")) {
            return (
                <span title={error}>
                    <FormattedMessage id="memberFiles.dataTable.errors.noHealthOrTravelGuarantee" />
                </span>
            );
        }

        // For any other errors, display the raw error with title for detail access
        return <span title={error}>
            <FormattedMessage id="memberFiles.dataTable.errors.technicalError" values={{ error: error }} />
        </span>;
    }

    /**
     * Determines if a member record is the most recent for that member_id.
     * Compares dates first, then guid as tiebreaker to ensure only one checkbox per member.
     */
    isLatestForMember(member) {
        if (!this.props.memberFiles || !member.target) {
            return false;
        }

        const memberId = member.target.memberId;
        const memberDate = member.date;
        const memberGuid = member.guid;

        const memberRecords = this.props.memberFiles.filter((m) => m.target && m.target.memberId === memberId);

        if (memberRecords.length === 0) {
            return false;
        }

        const sorted = memberRecords.sort((a, b) => {
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);

            if (dateB !== dateA) {
                return dateB - dateA;
            }

            const guidA = a.guid || "";
            const guidB = b.guid || "";
            return guidB.localeCompare(guidA);
        });

        return sorted[0].date === memberDate && sorted[0].guid === memberGuid;
    }

    /**
     * Checks if a member has one or more files defined.
     */
    hasMemberFiles(member) {
        return member.fileList && member.fileList.length > 0;
    }

    handleCheckboxChange(memberGuid) {
        const { selectedMembers } = this.state;
        const newSelected = new Set(selectedMembers);

        if (newSelected.has(memberGuid)) {
            newSelected.delete(memberGuid);
        } else {
            newSelected.add(memberGuid);
        }

        this.setState({ selectedMembers: newSelected });
    }

    getVisibleMemberGuids() {
        if (!this.props.memberFiles) {
            return [];
        }
        return this.props.memberFiles
            .filter((member) => this.isLatestForMember(member) && this.hasMemberFiles(member))
            .map((member) => member.guid);
    }


    handleRegenerateSelected() {
        const { selectedMembers } = this.state;
        const { memberFiles, clientId } = this.props;

        // Build list of member objects with their associated files
        const membersToRegenerate = [];
        memberFiles.forEach((member) => {
            if (selectedMembers.has(member.guid)) {
                membersToRegenerate.push({
                    memberId: member.target.memberId,
                    files: member.fileList || [],
                });
            }
        });

        if (membersToRegenerate.length === 0) {
            return;
        }

        // Call regenerate endpoint
        const model = MEMBER_FILES_REGENERATION.path.replace("{}", clientId);
        simpleFetch("post", model, {
            body: { members: membersToRegenerate },
        })
            .then(() => {
                toast(<FormattedMessage id="memberFiles.dataTable.toasts.regenerateInitiated" />, {
                    type: toast.TYPE.SUCCESS,
                    autoClose: 5000,
                });
            })
            .catch((error) => {
                // Handle specific error codes from API
                let errorMessageId = "memberFiles.dataTable.toasts.regenerateError";

                if (error && error.status === 400) {
                    const responseData = error.data;
                    const detail = responseData && responseData.detail;

                    if (typeof detail === "object" && detail) {
                        if (detail.error_code === "all_members_archived_error") {
                            errorMessageId = "memberFiles.dataTable.toasts.allMembersArchivedError";
                        } else if (detail.error_code === "all_members_non_eligible_error") {
                            errorMessageId = "memberFiles.dataTable.toasts.allMembersNonEligibleError";
                        }
                    }
                }

                toast(<FormattedMessage id={errorMessageId} />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
            })
            .finally(() => {
                // Clear selection after request
                this.setState({ selectedMembers: new Set() });
            });
    }

    getCols(visibleGuids) {
        let cols = [
            {
                name: <FormattedMessage id="memberFiles.dataTable.name" />,
                key: "name",
                display: (member) => (
                    <Link
                        to={`/dashboard/clients/${this.props.clientId}/members/${member.target.memberId}/profile`}
                        title={`${member.target.firstName} ${member.target.lastName}`}
                    >
                        {member.target.firstName} {member.target.lastName}
                    </Link>
                ),
                widthPct: 20,
            },
            {
                name: <FormattedMessage id="memberFiles.dataTable.status" />,
                key: "generationStatus",
                display: (member) => {
                    if (member.generationStatus === "FAILURE") {
                        return (
                            <div className="status status-member-file-failure">
                                <span>
                                    <FormattedMessage id="memberFiles.dataTable.statuses.failure" />
                                </span>
                            </div>
                        );
                    }
                    return (
                        <div className="status status-member-file-start">
                            <span>
                                <FormattedMessage id="memberFiles.dataTable.statuses.started" />
                            </span>
                        </div>
                    );
                },
                widthPct: 10,
            },
            {
                name: <FormattedMessage id="memberFiles.dataTable.coverageDate" />,
                key: "memberCoverageDate",
                display: (member) => <span>{member.target.memberCoverageDate}</span>,
                widthPct: 10,
            },
            {
                name: <FormattedMessage id="memberFiles.dataTable.generationDate" />,
                key: "date",
                display: (member) => {
                    return <span>{toDateOnly(member.date)}</span>;
                },
                widthPct: 10,
            },
            {
                name: <FormattedMessage id="memberFiles.dataTable.files" />,
                display: (member) => {
                    if (member.fileList) {
                        let fileStr = "";

                        member.fileList.forEach((f) => {
                            switch (f) {
                                case "MAJOR_CARD":
                                    fileStr += `${this.context.intl.formatMessage({
                                        id: "memberFiles.dataTable.majorCard",
                                    })},`;
                                    break;
                            }
                        });
                        if (fileStr && fileStr.endsWith(",")) {
                            fileStr = fileStr.slice(0, -1);
                        }
                        return <span>{fileStr}</span>;
                    }

                    return <span>N/A</span>;
                },
                widthPct: 20,
            },
            {
                name: <FormattedMessage id="memberFiles.dataTable.error" />,
                key: "error",
                display: (member) => this.renderErrorMessage(member),
                widthPct: 20,
            },
            {
                // Select/deselect all visible (latest) members checkbox
                name: (
                    <input
                        type="checkbox"
                        onChange={() => {
                            if (this.state.selectedMembers.size === visibleGuids.length) {
                                this.setState({ selectedMembers: new Set() });
                            } else {
                                this.setState({ selectedMembers: new Set(visibleGuids) });
                            }
                        }}
                        checked={this.state.selectedMembers.size === visibleGuids.length && visibleGuids.length > 0}
                        disabled={visibleGuids.length === 0 || this.props.clientStatusType !== ACTIVE.value}
                        title={this.context.intl.formatMessage({
                            id: "memberFiles.dataTable.selectAll",
                        })}
                    />
                ),
                display: (member) => {
                    // Only show checkbox for the most recent generation per member
                    if (!this.isLatestForMember(member)) {
                        return;
                    }

                    // Disable checkbox if member has no files
                    const hasFiles = this.hasMemberFiles(member);
                    const { clientStatusType } = this.props;

                    return (
                        <input
                            type="checkbox"
                            onChange={() => this.handleCheckboxChange(member.guid)}
                            checked={this.state.selectedMembers.has(member.guid)}
                            disabled={!hasFiles || clientStatusType !== ACTIVE.value}
                            title={
                                !hasFiles
                                    ? this.context.intl.formatMessage({
                                          id: "memberFiles.dataTable.noFileMessage",
                                      })
                                    : ""
                            }
                        />
                    );
                },
                widthPct: 5,
            },
        ];

        return cols;
    }

    getData(offset, limit, q, sortBy, filters) {
        const modelWithPath = {
            ...MEMBER_FILES,
            path: MEMBER_FILES.path.replace("{}", this.props.clientId),
        };

        return this.props.dataActions.getDataList(modelWithPath, offset, limit, q, sortBy, filters);
    }

    render() {
        const { selectedMembers } = this.state;
        const { clientStatusType } = this.props;

        const visibleGuids = this.getVisibleMemberGuids();

        return (
            <div>
                {selectedMembers.size > 0 && clientStatusType === ACTIVE.value && (
                    <div className="regenerate-btn">
                        <button className="btn btn-primary" onClick={this.handleRegenerateSelected}>
                            <FormattedMessage id="memberFiles.dataTable.regenerateButton" />
                            {` (${selectedMembers.size})`}
                        </button>
                    </div>
                )}
                <DataTable
                    ref="dataTable"
                    title={<FormattedMessage id="client.pages.memberFiles" />}
                    getData={this.getData}
                    cols={this.getCols(visibleGuids)}
                    sortBy={this.props.sortBy}
                    offset={this.props.offset}
                    data={this.props.memberFiles}
                    rowsPerPage={this.props.limit}
                    totalRows={this.props.total}
                    hideSearch={true}
                />
            </div>
        );
    }
}

MemberFilesDataTable.propTypes = {
    clientId: PropTypes.number,
    dataActions: PropTypes.object,
    offset: PropTypes.number,
    memberFiles: PropTypes.array,
    total: PropTypes.number,
    limit: PropTypes.number,
    clientStatusType: PropTypes.string,
    sortBy: PropTypes.object
};

MemberFilesDataTable.contextTypes = {
    intl: intlShape,
};

function mapStateToProps(state) {
    const client = state.data.clients.active;

    const data = state.data.memberFiles || {};
    const pagination = data.pagination || {};

    return {
        limit: pagination.limit || 50,
        offset: pagination.offset || 0,
        sortBy: data.sortBy || ["date:asc"],
        total: pagination.totalCount || 0,
        memberFiles: data.items || [],
        clientId: client.clientId,
        clientStatusType: client.clientStatusType,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dataActions: bindActionCreators(dataActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MemberFilesDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/member-files/MemberFilesDataTable.js